import { useEffect, useState } from "react";
import type { BrokerPreset } from "@yct/core";
import { api, type Broker } from "../lib/api.js";
import { AuthGate } from "../components/AuthGate.js";
import { ErrorText, Field } from "../components/ui.js";

export function BrokersPage() {
  return (
    <AuthGate>
      <BrokersInner />
    </AuthGate>
  );
}

const pct = (r: number) => (r * 100).toFixed(3).replace(/\.?0+$/, "");

function BrokersInner() {
  const [brokers, setBrokers] = useState<Broker[]>([]);
  const [presets, setPresets] = useState<BrokerPreset[]>([]);
  const [presetId, setPresetId] = useState("sbi");
  const [error, setError] = useState<unknown>(null);

  const reload = () => api.brokers().then((r) => setBrokers(r.brokers)).catch(setError);

  useEffect(() => {
    void reload();
    api.brokerPresets().then((r) => setPresets(r.presets)).catch(setError);
  }, []);

  const add = async () => {
    setError(null);
    try {
      await api.createBroker({ presetId });
      await reload();
    } catch (e) {
      setError(e);
    }
  };

  return (
    <section>
      <h1>証券会社設定</h1>
      <p className="muted">初期値は目安です。各社の公式ページで現行の料率・手数料を確認して修正してください。</p>
      <div className="row">
        <select className="input" value={presetId} onChange={(e) => setPresetId(e.target.value)}>
          {presets.map((p) => (
            <option key={p.presetId} value={p.presetId}>
              {p.name}
            </option>
          ))}
        </select>
        <button className="btn primary" type="button" onClick={add}>
          追加
        </button>
      </div>
      <ErrorText error={error} />
      {brokers.map((b) => (
        <BrokerCard key={b.id} broker={b} onChanged={reload} />
      ))}
      {brokers.length === 0 && <p className="muted">まだ証券会社がありません。上から追加してください。</p>}
    </section>
  );
}

function BrokerCard({ broker, onChanged }: { broker: Broker; onChanged: () => Promise<unknown> }) {
  const [edit, setEdit] = useState(false);
  const [name, setName] = useState(broker.name);
  const [seido, setSeido] = useState(pct(broker.loanRateSeido));
  const [short, setShort] = useState(pct(broker.loanRateIppanShort));
  const [long, setLong] = useState(pct(broker.loanRateIppanLong));
  const [adjSeido, setAdjSeido] = useState(pct(broker.dividendAdjRateSeido));
  const [adjIppan, setAdjIppan] = useState(pct(broker.dividendAdjRateIppan));
  const [plus, setPlus] = useState(broker.enableDividendPlus);
  const [error, setError] = useState<unknown>(null);

  const save = async () => {
    setError(null);
    try {
      await api.updateBroker(broker.id, {
        name,
        config: {
          loanRateSeido: Number(seido) / 100,
          loanRateIppanShort: Number(short) / 100,
          loanRateIppanLong: Number(long) / 100,
          dividendAdjRateSeido: Number(adjSeido) / 100,
          dividendAdjRateIppan: Number(adjIppan) / 100,
          enableDividendPlus: plus,
        },
      });
      await onChanged();
      setEdit(false);
    } catch (e) {
      setError(e);
    }
  };

  const remove = async () => {
    if (!confirm(`${broker.name} を削除しますか？`)) return;
    await api.deleteBroker(broker.id);
    await onChanged();
  };

  if (!edit) {
    return (
      <div className="card">
        <div className="row between">
          <h2>{broker.name}</h2>
          <div>
            <button className="btn" type="button" onClick={() => setEdit(true)}>
              編集
            </button>
            <button className="btn danger" type="button" onClick={remove}>
              削除
            </button>
          </div>
        </div>
        <dl className="dl">
          <dt>貸株料 制度</dt>
          <dd>{pct(broker.loanRateSeido)}%</dd>
          <dt>貸株料 一般短期</dt>
          <dd>{pct(broker.loanRateIppanShort)}%</dd>
          <dt>貸株料 一般無期限</dt>
          <dd>{pct(broker.loanRateIppanLong)}%</dd>
          <dt>配当落調整金 制度 / 一般</dt>
          <dd>
            {pct(broker.dividendAdjRateSeido)}% / {pct(broker.dividendAdjRateIppan)}%
          </dd>
          <dt>配当プラス計算</dt>
          <dd>{broker.enableDividendPlus ? "あり" : "なし"}</dd>
          <dt>手数料</dt>
          <dd>
            現物買 {feeSummary(broker.feeBuy)} / 信用売 {feeSummary(broker.feeSellMargin)} / 現渡 {feeSummary(broker.feeGenwatashi)}
          </dd>
        </dl>
      </div>
    );
  }

  return (
    <div className="card form">
      <Field label="名前">
        <input className="input" value={name} onChange={(e) => setName(e.target.value)} />
      </Field>
      <Field label="貸株料 制度（%）">
        <input className="input" inputMode="decimal" value={seido} onChange={(e) => setSeido(e.target.value)} />
      </Field>
      <Field label="貸株料 一般短期（%）">
        <input className="input" inputMode="decimal" value={short} onChange={(e) => setShort(e.target.value)} />
      </Field>
      <Field label="貸株料 一般無期限（%）">
        <input className="input" inputMode="decimal" value={long} onChange={(e) => setLong(e.target.value)} />
      </Field>
      <Field label="配当落調整金 制度（%）">
        <input className="input" inputMode="decimal" value={adjSeido} onChange={(e) => setAdjSeido(e.target.value)} />
      </Field>
      <Field label="配当落調整金 一般（%）">
        <input className="input" inputMode="decimal" value={adjIppan} onChange={(e) => setAdjIppan(e.target.value)} />
      </Field>
      <label className="check">
        <input type="checkbox" checked={plus} onChange={(e) => setPlus(e.target.checked)} /> 配当プラスを計算に入れる（特定口座・源泉徴収あり・損益通算前提）
      </label>
      <p className="muted">手数料の段階表の編集は後日対応。現在はプリセット値を使います。</p>
      <ErrorText error={error} />
      <div className="row">
        <button className="btn primary" type="button" onClick={save}>
          保存
        </button>
        <button className="btn" type="button" onClick={() => setEdit(false)}>
          キャンセル
        </button>
      </div>
    </div>
  );
}

function feeSummary(rule: Broker["feeBuy"]): string {
  switch (rule.type) {
    case "zero":
      return "0円";
    case "flat":
      return `${rule.amount}円`;
    case "table":
      return "段階制";
  }
}
